import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, IconButton, ToggleButtonGroup, ToggleButton,
  CircularProgress, Divider, Chip, Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { InteractiveLineChart, InteractiveBarChart, InteractivePieChart, InteractiveDonutChart } from './chartsLazy'; // Perf 3g
import { widgetDataApi } from '../../services/api';
import type { BucketBy, WidgetConfig } from '../../types';

interface WidgetDetailDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  metricId?: number;
  widgetType: string;
  config: WidgetConfig;
}

const WidgetDetailDialog: React.FC<WidgetDetailDialogProps> = ({
  open, onClose, title, metricId, widgetType, config,
}) => {
  const [bucketBy, setBucketBy] = useState<BucketBy>(config.xAxisRange || 'weeks');
  useEffect(() => { setBucketBy(config.xAxisRange || 'weeks'); }, [config.xAxisRange]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<{
    currentValue: number;
    previousValue: number | null;
    changePercent: number | null;
    changeAbsolute: number | null;
    trend: 'up' | 'down' | 'flat';
    periodLabel: string;
    previousPeriodLabel: string | null;
    timeSeries: Array<{ date: string; value: number; label: string }>;
    breakdown: Array<{ label: string; value: number }>;
    metricUnit: string | null;
    metricName: string | null;
    isRateMetric: boolean;
  } | null>(null);

  const loadData = useCallback(async () => {
    if (!metricId || !open) return;
    setLoading(true);
    try {
      const result = await widgetDataApi.aggregate({
        metricId,
        dateStart: config.dateStart || undefined,
        dateEnd: config.dateEnd || undefined,
        bucketBy,
        calculation: config.calculation || undefined,
        comparisonPeriod: config.comparisonPeriod || 'previous_period',
      });
      if (result.success && result.data) {
        setData(result.data);
      }
    } catch (err) {
      console.error('Failed to load widget detail data:', err);
    } finally {
      setLoading(false);
    }
  }, [metricId, open, bucketBy, config]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const trendColor = data?.trend === 'up' ? '#4caf50' : data?.trend === 'down' ? '#CF6679' : '#999';
  const TrendIcon = data?.trend === 'up' ? TrendingUpIcon : data?.trend === 'down' ? TrendingDownIcon : TrendingFlatIcon;
  const isRate = data?.isRateMetric || false;
  const unit = config.unit || (isRate ? '%' : '');
  const prefix = config.prefix || '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{
      sx: { borderRadius: 3, overflow: 'hidden', maxWidth: 960, mx: 'auto' }
    }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{title}</Typography>
          {data && (
            <Typography variant="caption" color="text.secondary">{data.periodLabel}</Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Summary row */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 200px', p: 2, bgcolor: 'action.hover', borderRadius: '6px' }}>
                <Typography variant="caption" color="text.secondary">Current Period</Typography>
                <Typography variant="h4" fontWeight={700}>
                  {prefix}{data.currentValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}{unit}
                </Typography>
              </Box>
              {data.previousValue != null && (
                <Box sx={{ flex: '1 1 200px', p: 2, bgcolor: 'action.hover', borderRadius: '6px' }}>
                  <Typography variant="caption" color="text.secondary">Previous Period</Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ opacity: 0.7 }}>
                    {prefix}{data.previousValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}{unit}
                  </Typography>
                </Box>
              )}
              <Box sx={{ flex: '1 1 200px', p: 2, bgcolor: 'action.hover', borderRadius: '6px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="caption" color="text.secondary">Change</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendIcon sx={{ color: trendColor, fontSize: 28 }} />
                  <Typography variant="h5" fontWeight={700} sx={{ color: trendColor }}>
                    {data.changePercent != null ? `${data.changePercent > 0 ? '+' : ''}${data.changePercent.toFixed(1)}%` : 'N/A'}
                  </Typography>
                </Box>
                {data.changeAbsolute != null && (
                  <Typography variant="caption" color="text.secondary">
                    {data.changeAbsolute > 0 ? '+' : ''}{prefix}{data.changeAbsolute.toLocaleString()}{unit} absolute
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Time range toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" fontWeight={600}>Trend</Typography>
              <ToggleButtonGroup
                value={bucketBy}
                exclusive
                onChange={(_, v) => v && setBucketBy(v)}
                size="small"
              >
                <ToggleButton value="days">Days</ToggleButton>
                <ToggleButton value="weeks">Weeks</ToggleButton>
                <ToggleButton value="months">Months</ToggleButton>
                <ToggleButton value="years">Years</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Main chart */}
            <Box sx={{ minHeight: 340 }}>
              {(widgetType === 'line' || widgetType === 'number_trend' || widgetType === 'area' || widgetType === 'count') ? (
                <InteractiveLineChart
                  data={data.timeSeries}
                  height={340}
                  showArea={widgetType === 'area' || widgetType === 'line'}
                  showPoints
                  unit={unit}
                  prefix={prefix}
                  colorTheme={config.colorTheme}
                  goalValue={config.goalValue}
                  showGoalLine={config.showGoalLine}
                  isRateMetric={isRate}
                />
              ) : widgetType === 'bar' ? (
                <InteractiveBarChart
                  data={data.timeSeries.map(p => ({ label: p.label, value: p.value }))}
                  height={340}
                  unit={unit}
                  prefix={prefix}
                  colorTheme={config.colorTheme}
                  orientation={config.barOrientation}
                  isRateMetric={isRate}
                />
              ) : widgetType === 'pie' ? (
                <InteractivePieChart
                  data={data.breakdown}
                  height={300}
                  unit={unit}
                  prefix={prefix}
                  colorTheme={config.colorTheme}
                />
              ) : widgetType === 'donut' ? (
                <InteractiveDonutChart
                  data={data.breakdown}
                  height={300}
                  unit={unit}
                  prefix={prefix}
                  colorTheme={config.colorTheme}
                  centerValue={`${prefix}${data.currentValue.toLocaleString()}`}
                  centerLabel="Total"
                />
              ) : (
                <InteractiveLineChart data={data.timeSeries} height={300} unit={unit} prefix={prefix} />
              )}
            </Box>

            <Divider />

            {/* Data table */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Data Breakdown</Typography>
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Value</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.timeSeries.map((point, idx) => {
                      const prev = idx > 0 ? data.timeSeries[idx - 1].value : null;
                      const change = prev != null ? point.value - prev : null;
                      const changePct = prev != null && prev !== 0
                        ? ((point.value - prev) / Math.abs(prev)) * 100 : null;
                      return (
                        <TableRow key={point.date} hover>
                          <TableCell>{point.label}</TableCell>
                          <TableCell align="right">
                            {prefix}{point.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}{unit}
                          </TableCell>
                          <TableCell align="right">
                            {change != null ? (
                              <Chip
                                size="small"
                                label={changePct != null ? `${change > 0 ? '+' : ''}${changePct.toFixed(1)}%` : '—'}
                                sx={{
                                  bgcolor: change > 0 ? 'rgba(76,175,80,0.15)' : change < 0 ? 'rgba(207,102,121,0.15)' : 'action.hover',
                                  color: change > 0 ? 'var(--status-on-track)' : change < 0 ? '#CF6679' : 'text.secondary',
                                  fontWeight: 600,
                                  fontSize: '12.5px',
                                }}
                              />
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Typography color="text.secondary">
              {metricId ? 'Failed to load data' : 'No metric configured'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WidgetDetailDialog;
